import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BedDouble, Snowflake } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const G = "#00604A", O = "#F45118", M = "#667085";
const TYPE = { lower_bunk: "Lower bunk", upper_bunk: "Upper bunk", single: "Single" };

function BedRequestDialog({ pick, onClose }) {
  const { user, authFetch } = useAuth();
  const [f, setF] = useState({ plan: "monthly", check_in: new Date().toISOString().slice(0, 10), check_out: "", phone: "", note: "" });
  useEffect(() => { if (user) setF((x) => ({ ...x, phone: user.phone || "" })); }, [user]);
  const send = async () => {
    try {
      await authFetch({ method: "post", url: `${API}/homestay/flatmate-beds/${pick.bed.id}/request`, data: { ...f, check_out: f.check_out || null, note: f.note || null } });
      toast.success(`Request sent to ${pick.prop.name}. You'll be notified once approved.`); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="flatmate-bed-dialog">
        <DialogHeader><DialogTitle>Book bed {pick.bed.label} · {pick.prop.name}</DialogTitle></DialogHeader>
        <div className="text-xs" style={{ color: M }}>{pick.prop.locality}, {pick.prop.city} · ₹{pick.bed.monthly_rate}/mo · ₹{pick.bed.nightly_rate}/night</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <select data-testid="fbed-plan" value={f.plan} onChange={set("plan")} className="rounded-xl border-2 p-2"><option value="monthly">Monthly</option><option value="nightly">Nightly</option></select>
          <input data-testid="fbed-phone" placeholder="Mobile" value={f.phone} onChange={set("phone")} className="rounded-xl border-2 p-2" />
          <label className="text-xs" style={{ color: M }}>Check-in<input type="date" data-testid="fbed-checkin" value={f.check_in} onChange={set("check_in")} className="mt-1 w-full rounded-xl border-2 p-2 text-sm" /></label>
          <label className="text-xs" style={{ color: M }}>Check-out (optional)<input type="date" data-testid="fbed-checkout" value={f.check_out} onChange={set("check_out")} className="mt-1 w-full rounded-xl border-2 p-2 text-sm" /></label>
          <input data-testid="fbed-note" placeholder="Note for the owner (optional)" value={f.note} onChange={set("note")} className="col-span-2 rounded-xl border-2 p-2" />
        </div>
        <button data-testid="fbed-submit" onClick={send} className="rounded-full py-2.5 text-white font-bold text-sm" style={{ background: O }}>Send booking request</button>
      </DialogContent>
    </Dialog>
  );
}

function PropBeds({ prop, onPick }) {
  return (
    <div data-testid={`fbeds-prop-${prop.id}`} className="shrink-0 w-80 rounded-3xl border bg-white p-4" style={{ borderColor: "#00604A22" }}>
      <div className="font-bold" style={{ color: G }}>{prop.name}</div>
      <div className="text-xs" style={{ color: M }}>{prop.locality}, {prop.city} · <b style={{ color: O }}>{prop.vacant} beds free</b></div>
      <div className="mt-3 grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
        {prop.beds.map((b) => (
          <button key={b.id} data-testid={`fbed-${b.id}`} onClick={() => onPick({ prop, bed: b })}
            className="rounded-xl border-2 p-2 text-left transition-transform hover:-translate-y-0.5" style={{ borderColor: "#FF8C1A", background: "#FFF6EC" }}>
            <div className="text-xs font-bold inline-flex items-center gap-1"><BedDouble className="w-3 h-3" />{b.label}{b.ac && <Snowflake className="w-3 h-3 text-sky-500" />}</div>
            <div className="text-[10px]" style={{ color: M }}>{TYPE[b.bed_type] || "Bed"}</div>
            <div className="text-[11px] font-bold" style={{ color: G }}>₹{b.monthly_rate}/mo</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FlatmateBedsStrip({ city }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [pick, setPick] = useState(null);
  useEffect(() => { axios.get(`${API}/homestay/flatmate-beds${city ? `?city=${encodeURIComponent(city)}` : ""}`).then((r) => setItems(r.data.items)).catch(() => {}); }, [city]);
  if (!items.length) return null;
  const onPick = (p) => (user ? setPick(p) : nav(`/login?next=${encodeURIComponent(window.location.pathname)}`));
  return (
    <div data-testid="flatmate-beds-strip" className="mt-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: O }}>HomeStay Feel · live</div>
          <h2 className="font-display text-2xl font-bold" style={{ color: G }}>Beds available right now</h2>
        </div>
        <div className="text-xs" style={{ color: M }}>Tap a bed to request it</div>
      </div>
      <div className="mt-3 flex gap-4 overflow-x-auto pb-2">{items.map((p) => <PropBeds key={p.id} prop={p} onPick={onPick} />)}</div>
      {pick && <BedRequestDialog pick={pick} onClose={() => setPick(null)} />}
    </div>
  );
}
